import { Box, Tooltip, Typography } from "@mui/material";
import { Favorite, FolderShared, SupportAgent } from "@mui/icons-material";
import { extractPlainTextPreview } from "../../../../utils/removeHtmlTagsAndEntities";
import { formatDateCompact } from "../../../../utils/dateUtils";
import { useNoteCategoryChoices } from "../../case-management-notes/useNoteCategoryChoices";
import { getChoiceLabel } from "../../../../utils/choiceUtils";
import { useNoteTitleChoices } from "../../case-management-notes/useNoteTitleChoices";
import { ColoredLabelChip } from "../../../../components/chips/ColoredLabelChip";
import { BaseTimelineItem } from "./BaseTimelineItem";
import { LogbookNoteItem } from "../../types";

type LogbookNoteItemProps = {
	note: LogbookNoteItem;
	isSelected: boolean;
	onClick: () => void;
	isMainTimeline?: boolean;
	linkUrl?: string;
};

const noteAccent = "#78909c";

export const LogbookNoteItemComponent = ({ note, isSelected, onClick, isMainTimeline = false, linkUrl }: LogbookNoteItemProps) => {
	const categoryChoices = useNoteCategoryChoices();
	const titleChoices = useNoteTitleChoices();
	const { fullPlainText, preview } = extractPlainTextPreview(note.content ?? "");
	const dateLabel = formatDateCompact(note.date_created);
	const categoryLabel = getChoiceLabel(categoryChoices, note.category ?? "customer_support");
	const titleChoice = titleChoices.find((choice) => choice.value === note.title);
	const iconColor = titleChoice?.color ?? noteAccent;

	return (
		<BaseTimelineItem config={{
			accentColor: noteAccent,
			icon: note.category === "client_owner"
				? <FolderShared sx={{ fontSize: 14, color: iconColor }} />
				: note.category === "psychology"
				? <Favorite sx={{ fontSize: 14, color: iconColor }} />
				: <SupportAgent sx={{ fontSize: 14, color: iconColor }} />,
			isSelected,
			dotVariant: isMainTimeline ? undefined : "nested",
			linkUrl,
		}}>
			<Box onClick={onClick} sx={{ flex: 1, cursor: "pointer" }}>
				<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
					<Typography variant="body2" fontWeight={600} color={noteAccent}>{categoryLabel}</Typography>
					<Box sx={{ transform: "scale(0.8)", transformOrigin: "left center", display: "flex", gap: 0.5, alignItems: "center" }}>
						{titleChoice?.color && (
							<ColoredLabelChip label={titleChoice.text} color={titleChoice.color} />
						)}
					</Box>
				</Box>
				<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block", fontSize: "0.65rem" }}>
					{dateLabel}
				</Typography>
				<Box sx={{ mt: 0.5 }}>
					<Tooltip title={fullPlainText} placement="bottom-start" enterDelay={500}>
						<Typography variant="body2" color="text.secondary">{preview}</Typography>
					</Tooltip>
				</Box>
			</Box>
		</BaseTimelineItem>
	);
};
